import { eosnationConfig } from './environments/eosnation.config';
import { stageConfig } from './environments/stage.config';
import type { Environment, LocalOverrides, OperatorConfig } from './types';
import { validateConfigOrThrow } from './validator';

/**
 * Get environment from build-time or runtime variables
 */
function getEnvironment(): Environment {
    // First check build-time environment variable
    const buildEnv = import.meta.env.VITE_ENV as Environment | undefined;
    if (buildEnv) {
        return buildEnv;
    }

    // Fallback: detect from hostname for backward compatibility
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;

        if (hostname.includes('creator-eco-stage') || hostname.includes('deweb.app')) {
            return 'stage';
        }

        if (hostname.includes('roundtable.io') || hostname.includes('deweb-519a7') || hostname.includes('eosnation')) {
            return 'eosnation';
        }
    }

    // Default to stage for local development
    return 'stage';
}

/**
 * Load configuration for specified environment
 */
function loadConfigForEnv(env: Environment): OperatorConfig {
    switch (env) {
        case 'eosnation':
            return eosnationConfig;
        default:
            return stageConfig;
    }
}

/**
 * Load local overrides from .env.local (development only)
 */
function loadLocalOverrides(): LocalOverrides {
    const overrides: LocalOverrides = {};

    // Only apply overrides in development
    if (import.meta.env.DEV) {
        // Domain name override (for localhost development)
        if (import.meta.env.VITE_DOMAIN_NAME) {
            overrides.domainName = import.meta.env.VITE_DOMAIN_NAME;
        }

        // RPC endpoint override
        if (import.meta.env.VITE_RPC_BASE_URL) {
            overrides.rpcBaseUrl = import.meta.env.VITE_RPC_BASE_URL;
        }

        // Functions endpoint overrides
        if (import.meta.env.VITE_FUNCTIONS_BASE_URL) {
            overrides.functionsBaseUrl = import.meta.env.VITE_FUNCTIONS_BASE_URL;
        }

        if (import.meta.env.VITE_FRONTEND_FUNCTIONS_BASE_URL) {
            overrides.frontendFunctionsBaseUrl = import.meta.env.VITE_FRONTEND_FUNCTIONS_BASE_URL;
        }

        // Cloud Run override
        if (import.meta.env.VITE_CLOUD_RUN_BASE_URL) {
            overrides.cloudRunBaseUrl = import.meta.env.VITE_CLOUD_RUN_BASE_URL;
        }

        // Debug mode
        if (import.meta.env.VITE_DEBUG_MODE === 'true') {
            overrides.debugMode = true;
        }

        // Fast refresh
        if (import.meta.env.VITE_FAST_REFRESH === 'true') {
            overrides.fastRefresh = true;
        }

        // Local dev host
        if (import.meta.env.VITE_LOCAL_DEV_HOST) {
            overrides.localDevHost = import.meta.env.VITE_LOCAL_DEV_HOST;
        }
    }

    return overrides;
}

/**
 * Merge base configuration with local overrides
 */
function mergeConfigs(base: OperatorConfig, overrides: LocalOverrides): OperatorConfig {
    const merged = { ...base };

    // Apply domain name override (highest priority)
    if (overrides.domainName) {
        merged.domainName = overrides.domainName;
        console.log('[Config] Overriding domain name:', overrides.domainName);
    }

    // Apply URL overrides
    if (overrides.rpcBaseUrl) {
        merged.rpcBaseUrl = overrides.rpcBaseUrl;
        console.log('[Config] Overriding RPC URL:', overrides.rpcBaseUrl);
    }

    if (overrides.functionsBaseUrl) {
        merged.functionsBaseUrl = overrides.functionsBaseUrl;
        console.log('[Config] Overriding Functions URL:', overrides.functionsBaseUrl);
    }

    if (overrides.frontendFunctionsBaseUrl) {
        merged.frontendFunctionsBaseUrl = overrides.frontendFunctionsBaseUrl;
        console.log('[Config] Overriding Frontend Functions URL:', overrides.frontendFunctionsBaseUrl);
    }

    if (overrides.cloudRunBaseUrl) {
        merged.cloudRunBaseUrl = overrides.cloudRunBaseUrl;
        console.log('[Config] Overriding Cloud Run URL:', overrides.cloudRunBaseUrl);
    }

    // Apply domain name override for local development (legacy support)
    if (overrides.localDevHost && typeof window !== 'undefined') {
        merged.domainName = overrides.localDevHost;
        console.log('[Config] Using local dev host:', overrides.localDevHost);
    }

    return merged;
}

// Singleton configuration instance
let cachedConfig: OperatorConfig | null = null;

/**
 * Get unified operator configuration
 *
 * This is the primary entry point for accessing configuration.
 * Config is validated on first access and cached for subsequent calls.
 *
 * @throws {Error} If configuration is invalid
 * @returns {OperatorConfig} Validated and merged configuration
 *
 * @example
 * ```typescript
 * import { getConfig } from './config';
 *
 * const config = getConfig();
 * console.log('Using RPC endpoint:', config.rpcBaseUrl);
 * ```
 */
export function getConfig(): OperatorConfig {
    // Return cached config if available
    if (cachedConfig) {
        return cachedConfig;
    }

    // Determine environment
    const env = getEnvironment();
    console.log('[Config] Loading configuration for environment:', env);

    // Load base configuration
    const baseConfig = loadConfigForEnv(env);

    // Load and apply local overrides
    const overrides = loadLocalOverrides();
    const config = mergeConfigs(baseConfig, overrides);

    // Set domain name from window location if not specified
    if (!config.domainName && typeof window !== 'undefined') {
        const host = window.location.host;

        // For localhost development, use the environment-specific domain
        if (host.startsWith('localhost') || host.startsWith('127.0.0.1')) {
            // Use the domain from the loaded environment config
            config.domainName = env === 'eosnation' ? 'roundtable.io' : 'creator-eco-stage.web.app';
            console.log(`[Config] Localhost detected, using domain: ${config.domainName}`);
        } else {
            config.domainName = host.startsWith('www.') ? host.slice(4) : host;
        }
    }

    // Set Firebase auth domain if not specified
    if (!config.firebaseConfig.authDomain && config.domainName) {
        config.firebaseConfig.authDomain = config.domainName;
    }

    // Validate configuration
    try {
        validateConfigOrThrow(config);
        console.log('[Config] ✓ Configuration validated successfully');
    } catch (error) {
        console.error('[Config] Configuration validation failed!');
        throw error;
    }

    // Cache for subsequent calls
    cachedConfig = config;

    return config;
}

/**
 * Reset cached configuration (useful for testing)
 */
export function resetConfigCache(): void {
    cachedConfig = null;
}

/**
 * Get current environment
 */
export function getCurrentEnvironment(): Environment {
    return getEnvironment();
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
    return import.meta.env.DEV;
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
    return import.meta.env.PROD;
}
